import { useCallback } from 'react'
import type { ComboVariation, Proposta, PropostaItem } from '@/types'
import { generateId } from './types'
import type { ComboData } from './types'
import { parseComboVariationsForEdit, parseCombosForEdit, validadeDateFromProposal } from './proposalFormState'

export function useProposalTabFormActions(params: {
  setItens: React.Dispatch<React.SetStateAction<PropostaItem[]>>
  setCombos: React.Dispatch<React.SetStateAction<ComboData[]>>
  setComboVariations: React.Dispatch<React.SetStateAction<ComboVariation[]>>
  setEditingId: (id: string | null) => void
  setValidadeAte: (v: string) => void
  setObservacoes: (v: string) => void
  setDesconto: (v: number) => void
  setShowForm: (v: boolean) => void
}) {
  const {
    setItens,
    setCombos,
    setComboVariations,
    setEditingId,
    setValidadeAte,
    setObservacoes,
    setDesconto,
    setShowForm,
  } = params

  const handleRemoveItem = useCallback(
    (id: string) => {
      setItens((prev) => prev.filter((i) => i.id !== id))
      setCombos((prev) =>
        prev
          .map((c) => ({ ...c, itemIds: c.itemIds.filter((iid) => iid !== id) }))
          .filter((c) => c.itemIds.length > 0),
      )
      setComboVariations((prev) =>
        prev.map((v) => ({ ...v, itemIds: v.itemIds.filter((iid) => iid !== id) })),
      )
    },
    [setItens, setCombos, setComboVariations],
  )

  const handleUpdateItemQty = useCallback(
    (id: string, qty: number) => {
      if (qty < 1) return
      setItens((prev) =>
        prev.map((i) => (i.id === id ? { ...i, quantidade: qty, total: qty * i.valorUnitario } : i)),
      )
    },
    [setItens],
  )

  const handleSetComboFinalPrice = useCallback(
    (comboGroupId: string, price: number) => {
      setItens((prev) =>
        prev.map((i) => (i.comboGroupId === comboGroupId ? { ...i, comboFinalPrice: price } : i)),
      )
    },
    [setItens],
  )

  const handleEditProposal = useCallback(
    (proposta: Proposta) => {
      setEditingId(proposta.id)
      setItens((proposta.itens ?? proposta.items ?? []).map((i) => ({ ...i })))
      setValidadeAte(validadeDateFromProposal(proposta))
      setObservacoes(proposta.observacoes ?? '')
      setDesconto(proposta.desconto)
      setComboVariations(parseComboVariationsForEdit(proposta))
      setCombos(parseCombosForEdit(proposta))
      setShowForm(true)
    },
    [setEditingId, setItens, setValidadeAte, setObservacoes, setDesconto, setComboVariations, setCombos, setShowForm],
  )

  const handleAddCombo = useCallback(
    () =>
      setCombos((prev) => [
        ...prev,
        { id: generateId(), nome: `Combo ${prev.length + 1}`, desconto: 0, itemIds: [] },
      ]),
    [setCombos],
  )

  const handleToggleComboItem = useCallback(
    (comboId: string, itemId: string) => {
      setCombos((prev) =>
        prev.map((c) => {
          if (c.id !== comboId) return c
          const hasItem = c.itemIds.includes(itemId)
          return {
            ...c,
            itemIds: hasItem ? c.itemIds.filter((id) => id !== itemId) : [...c.itemIds, itemId],
          }
        }),
      )
    },
    [setCombos],
  )

  const handleComboDesconto = useCallback(
    (comboId: string, val: number) => {
      setCombos((prev) => prev.map((c) => (c.id === comboId ? { ...c, desconto: val } : c)))
    },
    [setCombos],
  )

  const handleRemoveCombo = useCallback(
    (comboId: string) => {
      setCombos((prev) => prev.filter((c) => c.id !== comboId))
    },
    [setCombos],
  )

  return {
    handleRemoveItem,
    handleUpdateItemQty,
    handleSetComboFinalPrice,
    handleEditProposal,
    handleAddCombo,
    handleToggleComboItem,
    handleComboDesconto,
    handleRemoveCombo,
  }
}
